import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
  type NormalizedCacheObject,
  split
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { getCookie } from '@polestar/shared-front-myorder';
import { authHelper } from '~app/helpers/authHelper';
import { config } from '~root/src/config';

const createClient = ({ apiUrl = config.api.global } = {}) => {
  /**
   * Inject server-side rendered apollo initial state without using unsafe-inline scripts
   * blocked by Content-Security-Policy
   * @see object
   */
  const getInitialState = (): NormalizedCacheObject => {
    let initialState: unknown = null;
    const dataScripts = document.querySelectorAll('#__APOLLO_DATA__');

    // Ensure we only load one script from the page to prevent potential XSS attacks
    if (dataScripts.length === 1) {
      try {
        initialState = JSON.parse(dataScripts[0].innerHTML);
      } catch (error) {
        console.error('Unable to define apollo initial state', error);
      }
    }

    return initialState as NormalizedCacheObject;
  };

  // check if we are to create the dato apollo client for the preview site or the regular site
  let datoPreviewToken = '';
  try {
    datoPreviewToken = new URLSearchParams(window.location.search).get('preview') || '';
  } catch (error) {
    console.error('Unable to define dato preview token', error);
  }

  const httpSplit = split(
    (operation) => operation.getContext().dato === true || operation.getContext().api === 'DATO',
    ApolloLink.from([
      new ApolloLink((operation, forward) => {
        if (typeof window !== 'undefined') {
          operation.variables.origin = window.location.origin;
        }
        return forward(operation);
      }),
      new HttpLink({
        fetch: (uri, options) => {
          return fetch(
            (uri as string).replace(
              /__DATO_BASE_URL__/gi,
              datoPreviewToken ? config.dato.previewUrl : config.dato.baseUrl
            ),
            options
          );
        },
        uri: '__DATO_BASE_URL__',
        useGETForQueries: true // Take advantage of HTTP cache
      })
    ]),
    new HttpLink({
      credentials: 'include',
      fetch: (uri, options) => {
        return fetch((uri as string).replace(/__API_BASE_URL__/gi, apiUrl), options).then(
          async (response) => {
            const data = await response.clone().json();
            // treat graphql error with code UNAVAILABLE as network error
            if (data.errors) {
              for (const err of data.errors) {
                switch (err.extensions.code) {
                  case 'UNAVAILABLE':
                    throw new Error(err.message);
                }
              }
            }

            // eslint-disable-next-line promise/no-return-wrap
            return Promise.resolve(response);
          }
        );
      },
      uri: '__API_BASE_URL__',
      useGETForQueries: false // Take advantage of HTTP cache
    })
  );

  /**
   * @var ApolloClient
   */
  const client = new ApolloClient({
    link: ApolloLink.from([
      new ApolloLink((operation, forward) => {
        // add the authorization to the headers
        operation.setContext(({ headers = {} }) => {
          let authToken: string | undefined | null;
          const isDatoConext = operation.getContext().dato || operation.getContext().api === 'DATO';

          if (isDatoConext) {
            // Dato Token
            authToken = datoPreviewToken || config.dato.token;
          } else if (authHelper.isImpersonating) {
            // Impersonation Token
            authToken = authHelper.impersonationToken;
          } else {
            // PolestarId Token
            const token = getCookie<{ access_token: string }>('token');
            authToken = token?.access_token;
          }
          return {
            headers: {
              ...headers,
              ...(authToken && { authorization: `Bearer ${authToken}` })
            }
          };
        });

        return forward(operation);
      }),
      new RetryLink({ delay: { jitter: false } }),
      onError(({ graphQLErrors, networkError }) => {
        if (graphQLErrors) {
          graphQLErrors.map(({ message, locations, path }) =>
            console.warn(
              `[GraphQL Error] Message: ${message}. Locations: ${locations}. Path: ${path}.`
            )
          );
        }
        if (networkError) {
          console.error(`[Network Error] ${networkError}`);
        }
      }),
      httpSplit
    ]),
    // hydrate state from SSR
    cache: new InMemoryCache().restore(getInitialState()),
    credentials: 'include'
  });

  return client;
};

export const apolloClient = createClient();
